import { useState } from 'react';
import { DataTable, Pill, Td } from '../../components/ui';
import { api, fileUrl } from '../../lib/api';
import { DOC_LABELS, fmtDateTime } from '../../lib/format';
import type { Doc } from '../../shared/api';
import type { TabProps } from './types';

/**
 * Who may check a document by its printed ID. The contractor always can (it is
 * listed here); anyone else only if they share it — or, for a Claim of Lien or
 * Notice of Commencement once its recording is entered, because a recorded
 * document is public record anyway.
 */
function Verification({ jobId, doc, reload }: { jobId: string; doc: Doc; reload: () => void }) {
  const [busy, setBusy] = useState(false);
  if (!doc.publicId) return <span className="text-xs text-dim">—</span>;
  const toggle = async (shared: boolean) => {
    setBusy(true);
    try {
      await api.put(`/api/jobs/${jobId}/documents/${doc.id}/verify-sharing`, { shared });
      reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1">
      <p className="font-mono text-xs text-ink" title={doc.sha256 ? `SHA-256 ${doc.sha256}` : undefined}>
        {doc.publicId}
      </p>
      {doc.verifiable === 'public_record' ? (
        <Pill tone="ok">Public record: anyone can verify</Pill>
      ) : (
        <label className="flex items-center gap-1.5 text-xs text-muted">
          <input type="checkbox" disabled={busy} checked={doc.verifiable === 'shared'} onChange={(e) => void toggle(e.target.checked)} />
          Let others verify by this ID
        </label>
      )}
    </div>
  );
}

export default function DocumentsTab({ job, reload }: TabProps) {
  return (
    <div className="space-y-3">
      <DataTable head={['Document', 'ID', 'Generated', 'Sent', 'Delivered', '']} empty="No documents generated yet.">
        {job.documents.map((doc) => {
          const href = fileUrl(doc.pdfUrl);
          return (
            <tr key={doc.id} className="border-b border-border/60">
              <Td className="font-medium">{DOC_LABELS[doc.docType] ?? doc.docType}</Td>
              <Td>
                <Verification jobId={job.id} doc={doc} reload={reload} />
              </Td>
              <Td className="text-muted">{fmtDateTime(doc.generatedAt)}</Td>
              <Td className="text-muted">{fmtDateTime(doc.sentAt)}</Td>
              <Td>{doc.deliveredAt ? <Pill tone="ok">{fmtDateTime(doc.deliveredAt)}</Pill> : <span className="text-muted">—</span>}</Td>
              <Td>
                {href ? (
                  <a href={href} target="_blank" rel="noreferrer" className="text-sm font-semibold text-tint hover:underline">
                    Download
                  </a>
                ) : (
                  <span className="text-xs text-dim">No file</span>
                )}
              </Td>
            </tr>
          );
        })}
      </DataTable>
      <p className="text-xs text-dim">
        Each document LienGuardian issues carries its ID on every page and a fingerprint of the exact file. Anyone you let
        verify it can check, at app.lienguardian.ai/verify, that a copy is the unaltered original. They see the document type
        and issue date only.
      </p>
    </div>
  );
}
